import { Component, DestroyRef, Input, OnDestroy, OnInit } from '@angular/core';
import { DiscussionService } from '../../../services/discussion.service';
import { API, LinkedEntityDTO, TopicDTO, TopicFullDTO } from '../../../services/api/api';
import { BehaviorSubject } from 'rxjs';
import { UtilService } from '../../../services/util.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';

export interface PolymorphicEntityElement {
  id?: number | null;
  type: string | null;
  element: any | null;
}

@Component({
  selector: '[sb-discussion]',
  templateUrl: './sb-discussion.component.html',
  styleUrls: ['./sb-discussion.component.scss'],
  standalone: false,
})
export class SbDiscussionComponent implements OnInit, OnDestroy {
  @Input() set entityElement(val: PolymorphicEntityElement) {
    this._entityElement = val;
    if (this._entityElement) {
      this.entityKey.id = this._entityElement.element.id;
      this.entityKey.type = this._entityElement.type;
      this.discussionService.addDiscussableKey(this.entityKey);
    }
  }

  @Input() inline: boolean;
  @Input() inTable: boolean;
  @Input() inContext: boolean;

  @Input() set changes(val: boolean) {
    if (val) {
      if (!this.discussionService.discussions) {
        this.discussionService.discussions = new BehaviorSubject<TopicDTO[] | null>(null);
      }
      this.discussionService.requestDebounced();
      this.updateDiscussions();
    }
  }

  hasDiscussion: boolean;
  topic: TopicDTO;
  _entityElement: PolymorphicEntityElement;
  entityKey: LinkedEntityDTO;

  constructor(
    public discussionService: DiscussionService,
    public api: API,
    public util: UtilService,
    private destroyRef: DestroyRef
  ) {
    this.hasDiscussion = false;
    this.entityKey = new LinkedEntityDTO();
    this.inContext = false;
  }

  ngOnInit(): void {
    if (!this.discussionService.discussions) {
      this.discussionService.discussions = new BehaviorSubject<TopicDTO[] | null>(null);
    }
    if (this.discussionService.showRibbon || this.discussionService.showDiscussionSideBar) {
      this.updateDiscussions();
    }
  }

  updateDiscussions() {
    if (this._entityElement?.element) {
      this.discussionService.discussableElements.push(this._entityElement.element);
      this.discussionService.discussions.subscribe((data: TopicDTO[]) => {
        this.findDiscussion(data).then();
      });
    }
  }

  selectDiscussableGroup() {
    this.discussionService.ribbonClick();
    this.discussionService.cameFromSubTopic = true;
    this.discussionService.cameFromSubTopicMemory = true;
    this.discussionService.newTopic = false;
    this.discussionService.selectedTopic = null;
    this.discussionService.selectDiscussableGroup(this._entityElement.element.id);
    if (this.discussionService.selectedGroup?.topics.length === 1) {
      this.api
        .shared_Discussion_retrieveTopicById(this.discussionService?.selectedGroup?.topics[0]?.id)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe((mainPost: TopicFullDTO) => {
          this.discussionService.selectedTopic = mainPost;
        });
    }
    this.discussionService.openSideBar();
  }

  createNewTopic() {
    this.selectDiscussableGroup();
    this.discussionService.addNewTopic();
  }

  findDiscussion(topics: TopicDTO[]) {
    return new Promise(resolve => {
      if (!topics || topics.length === 0) {
        this.hasDiscussion = false;
        resolve(true);
      } else {
        this.iterateTopics(topics).then(() => {
          resolve(true);
        });
      }
    });
  }

  iterateTopics(topics: TopicDTO[]) {
    return new Promise(resolve => {
      for (const topic of topics) {
        if (topic.entity.type === this.entityKey.type) {
          if (topic.entity.id === this.entityKey.id) {
            this.hasDiscussion = true;
            return;
          }
          this.hasDiscussion = false;
        }
        this.hasDiscussion = false;
      }
      resolve(true);
    });
  }

  ngOnDestroy(): void {
    if (this.discussionService.discussions) {
      this.discussionService.discussions.unsubscribe();
      this.discussionService.discussions = null;
      this.discussionService.discussableElements = [];
      this.discussionService.keyArr = [];
    }
  }
}
